import { getRealtimeDatabaseCurrentTimestamp } from '@framework/firebase/rtdb';
import { ObjectRepository, PrimitiveValueRepository, RefBuilder, ServerValue } from '@framework/repository';
import { RTDBPath, NO_ASSIGN } from 'balus-common';
import type {
  GroupId,
  UserId,
  WorkspaceId,
  WorkspaceInternalPublicMemberRoleJSON,
  WorkspaceMemberRoleJSON,
  OperatorJSON,
  ModelingFormat,
} from 'balus-common';
import { createWorkspaceEntityRepository } from './workspace/repository';
import { WorkspaceMemberRole } from './workspace/vo/WorkspaceMemberRole';
import { WorkspaceSetting } from './workspace/vo/WorkspaceSetting';
import { Workspace } from './workspace/Workspace';
import { difference } from 'lodash-es';
import { GroupMemberRoles } from '@group/domain/GroupEntity/GroupMemberRoles';
import { WorkspaceMemberRoles } from './workspace/vo/WorkspaceMemberRoles';
import { trpcClient } from '@framework/trpcClient';

export class WorkspaceOperation {
  /**
   * 指定 groupId に対してワークスペースを作成し、 userId をワークスペース管理者として追加する。
   *
   * ワークスペースの作成時には、以下に対してデータを保存する。
   *   1. workspaces/{workspaceKey}
   *   2. workspace/assigned-group-workspace-index/{userId}/{groupId}/{workspaceId}
   *   3. group-contents/{groupKey}/workspace-keys/{workspaceKey}
   *
   * @param name
   * @param groupId
   * @param userId
   * @param onComplete
   */
  static async create(name: string, groupId: string, userId: string): Promise<WorkspaceId> {
    const groupEnableIndividualViewModelPermissionsRepo = new PrimitiveValueRepository<boolean>(
      RTDBPath.Group.enableIndividualViewModelPermissionsPath(groupId)
    );
    const groupIndividualViewModelPermission = (await groupEnableIndividualViewModelPermissionsRepo.get()) || false;
    const defaultModelingFormat =
      (await new PrimitiveValueRepository<ModelingFormat>(
        RTDBPath.Group.settingDefaultModelingFormatPath(groupId)
      ).get()) || 'CJK';

    const workspace = Workspace.buildNew(
      name,
      groupId,
      await getRealtimeDatabaseCurrentTimestamp(),
      groupIndividualViewModelPermission,
      defaultModelingFormat
    );
    return this.saveForCreate(workspace, userId, WorkspaceMemberRole.admin);
  }

  /**
   * 指定 groupId に対して、ユーザ userId のパーソナルワークスペースを作成します。
   * @param groupId
   * @param userId
   * @param name パーソナルワークスペース名（i18n で翻訳済み）
   * @returns
   */
  static async createPersonal(groupId: GroupId, userId: UserId, name: string): Promise<WorkspaceId> {
    const defaultModelingFormat =
      (await new PrimitiveValueRepository<ModelingFormat>(
        RTDBPath.Group.settingDefaultModelingFormatPath(groupId)
      ).get()) || 'CJK';

    const workspace = Workspace.buildNewPersonal(
      name,
      groupId,
      userId,
      await getRealtimeDatabaseCurrentTimestamp(),
      defaultModelingFormat
    );
    const ref = RefBuilder.ref(RTDBPath.Workspace.settingPath(workspace.id));
    const snapshot = await ref.get();

    // ワークスペースの設定が存在するならば、そのワークスペースに対してメンバー追加を行う。
    if (snapshot.val()) {
      await RefBuilder.ref().update({
        [RTDBPath.Workspace.memberRolePath(workspace.id, userId)]: WorkspaceMemberRole.editor,
        [RTDBPath.Workspace.assignedGroupWorkspaceIndexValuePath(userId, groupId, workspace.id)]: NO_ASSIGN,
      });
      return workspace.id;
    } else {
      return this.saveForCreate(workspace, userId, WorkspaceMemberRole.editor);
    }
  }

  /**
   * ワークスペースを新規作成する時に利用する保存処理
   */
  private static async saveForCreate(
    workspace: Workspace,
    userId: UserId,
    role: WorkspaceMemberRole
  ): Promise<WorkspaceId> {
    const groupId = workspace.groupId;

    await RefBuilder.ref().update({
      [RTDBPath.Workspace.workspaceEntityPath(workspace.id)]: workspace.dump(),
      [RTDBPath.Workspace.memberRolePath(workspace.id, userId)]: role,
      [RTDBPath.Group.workspaceIndexValuePath(groupId, workspace.id)]: NO_ASSIGN,
      [RTDBPath.Workspace.assignedGroupWorkspaceIndexValuePath(userId, groupId, workspace.id)]: NO_ASSIGN,
    });

    return workspace.id;
  }

  /**
   * ワークスペース名と説明を更新する。
   */
  static async update(
    workspaceId: WorkspaceId,
    name: string,
    description: string,
    imageUrl: string | null
  ): Promise<void> {
    await RefBuilder.ref(RTDBPath.Workspace.workspaceEntityPath(workspaceId)).update({
      name,
      description,
      imageUrl,
      updatedAt: ServerValue.TIMESTAMP,
    });
  }

  /**
   * 指定のワークスペースの設定を更新する。
   */
  static async updateSetting(workspaceId: WorkspaceId, workspaceSetting: WorkspaceSetting): Promise<void> {
    await RefBuilder.ref(RTDBPath.Workspace.workspaceEntityPath(workspaceId)).update({
      setting: workspaceSetting.dump(),
      updatedAt: ServerValue.TIMESTAMP,
    });
  }

  /**
   * 指定のワークスペースのグループ内公開設定を更新します。
   * プライベートからグループ内公開に変更する時には、グループメンバー全員をワークスペースに追加します。
   * 更新の成否をbooleanで返す。
   */
  static async updateInternalPublicSetting(
    workspaceId: WorkspaceId,
    value: WorkspaceInternalPublicMemberRoleJSON | null
  ): Promise<void> {
    const workspace = await new ObjectRepository(Workspace, RTDBPath.Workspace.workspaceEntityPath(workspaceId)).get();
    if (!workspace) {
      throw new Error(`WorkspaceEntity(id=${workspaceId}) is not found.`);
    }

    const workspaceMemberRoles = await new ObjectRepository(
      WorkspaceMemberRoles,
      RTDBPath.Workspace.memberRolesPath(workspaceId)
    ).get();
    if (!workspaceMemberRoles) {
      throw new Error(`WorkspaceMemberRoles(workspaceId=${workspaceId}) is not found.`);
    }

    const groupMemberRoles = await new ObjectRepository(
      GroupMemberRoles,
      RTDBPath.Group.memberRolesPath(workspace.groupId)
    ).get();
    if (!groupMemberRoles) {
      throw new Error(`GroupMemberRoles(groupId=${workspace.groupId}) is not found.`);
    }

    // グループ内公開設定の値を保存する。
    await RefBuilder.ref(RTDBPath.Group.internalPublicWorkspacePath(workspace.groupId, workspaceId)).set(value);

    // 保存するグループ内公開設定の値が null の場合には、メンバー追加処理をスキップして処理終了する。
    if (!value) {
      return;
    }

    // グループの "ゲスト" ロール以外のユーザで、ワークスペースに未所属のユーザを、このワークスペースに追加する
    const addingUserIds = difference(groupMemberRoles.regularUserIds(), workspaceMemberRoles.userIds());
    if (addingUserIds.length > 0) {
      await trpcClient.workspace.addMembers.mutate({ workspaceId, userIds: addingUserIds, role: value });
    }
  }

  /**
   * 指定のワークスペースから指定された複数のユーザを削除するための ref.update() に渡す値を生成して返します。
   * @param workspaceId
   * @param groupId
   * @param userIds
   * @returns
   */
  static async buildRemovableMemberValues(
    workspaceId: WorkspaceId,
    groupId: GroupId,
    userIds: UserId[]
  ): Promise<Record<string, null>> {
    const values: Record<string, null> = {};

    for (const userId of userIds) {
      values[RTDBPath.Workspace.memberRolePath(workspaceId, userId)] = null;
      values[RTDBPath.Workspace.assignedGroupWorkspaceIndexValuePath(userId, groupId, workspaceId)] = null;

      // グループのメンバーロールが "guest" の場合には、参加対象のワークスペースIDをインデックスを削除する
      const snapshot = await RefBuilder.ref(RTDBPath.Group.assignedWorkspaceIdGroupGuestPath(groupId, userId)).get();
      if (snapshot.val()) {
        values[RTDBPath.Group.assignedWorkspaceIdGroupGuestPath(groupId, userId)] = null;
      }
    }

    return values;
  }

  /**
   * 指定のワークスペースから指定された複数のユーザを削除します。
   * @param workspaceId
   * @param groupId
   * @param userIds
   */
  static async removeMembers(workspaceId: WorkspaceId, groupId: GroupId, userIds: UserId[]): Promise<void> {
    const groupMemberRoles = await new ObjectRepository(
      GroupMemberRoles,
      RTDBPath.Group.memberRolesPath(groupId)
    ).get();
    if (!groupMemberRoles) {
      throw new Error(`GroupMemberRoles(groupId=${groupId}) is not found.`);
    }

    const values = await this.buildRemovableMemberValues(workspaceId, groupId, userIds);
    await RefBuilder.ref().update(values);
  }

  /**
   * ワークスペースの論理削除を行う.
   *  * ワークスペースのメンバーを0人に変更
   *  * ワークスペースの共有設定を無効化
   *  * 論理削除日時、削除ユーザを記録
   */
  static async logicalDelete(workspaceId: WorkspaceId, trashedBy: UserId | OperatorJSON['email']): Promise<void> {
    // workspaceの取得
    const workspace = await createWorkspaceEntityRepository(workspaceId).get();
    if (!workspace) {
      throw new Error(`WorkspaceEntity(id=${workspaceId}) is not found.`);
    }
    // 既に論理削除済みならば、特に処理を行わずにreturnする
    if (workspace.trashedAt) return;

    // ワークスペースの公開設定を無効化
    await this.updateSetting(
      workspace.id,
      WorkspaceSetting.load({
        isPublicSpace: false,
        isViewModelURLShareable: false,
        enableIndividualViewModelPermissions: false,
        modelingFormat: 'CJK',
      })
    );

    // ワークスペースのグループ内公開設定を無効化
    await this.updateInternalPublicSetting(workspace.id, null);

    // ユーザー関係のレコードを全て削除
    const memberRepo = new ObjectRepository(WorkspaceMemberRoles, RTDBPath.Workspace.memberRolesPath(workspace.id));
    const memberRoles = await memberRepo.get();
    const memberIds = memberRoles?.userIds() || [];

    const values = await this.buildRemovableMemberValues(workspace.id, workspace.groupId, memberIds);

    await RefBuilder.ref().update({
      ...values,
      [`${RTDBPath.Workspace.workspaceEntityPath(workspace.id)}/updatedAt`]: ServerValue.TIMESTAMP,
      [`${RTDBPath.Workspace.workspaceEntityPath(workspace.id)}/trashedAt`]: ServerValue.TIMESTAMP,
      [`${RTDBPath.Workspace.workspaceEntityPath(workspace.id)}/trashedBy`]: trashedBy,
    });
  }

  static async updateMemberRole(
    workspaceId: WorkspaceId,
    userId: UserId,
    role: WorkspaceMemberRoleJSON
  ): Promise<void> {
    await RefBuilder.ref(RTDBPath.Workspace.memberRolePath(workspaceId, userId)).set(role);
  }

  /**
   * 指定のワークスペースに対して追加可能なユーザIDの一覧を返す。
   * グループのゲストロールの場合には、1つのワークスペースのみに参加可能であることも考慮して、
   * 追加可能なユーザIDのリストを返す。
   *
   * @param groupId
   * @param workspaceId
   * @returns
   */
  static async getAddableUserIds(
    groupId: GroupId,
    workspaceId: WorkspaceId
  ): Promise<{ userId: UserId; isGuest: boolean }[]> {
    const groupMemberRoles =
      (await new ObjectRepository(GroupMemberRoles, RTDBPath.Group.memberRolesPath(groupId)).get()) ||
      GroupMemberRoles.load({});

    const workspaceMemberRoles =
      (await new ObjectRepository(WorkspaceMemberRoles, RTDBPath.Workspace.memberRolesPath(workspaceId)).get()) ||
      WorkspaceMemberRoles.load({});

    const assignedGroupIdIndex =
      (await RefBuilder.ref(RTDBPath.Group.assignedWorkspaceIdGroupGuestsPath(groupId)).get()).val() || {};

    const availableUserIds = difference(
      difference(groupMemberRoles.userIds(), Object.keys(assignedGroupIdIndex)),
      workspaceMemberRoles.userIds()
    );

    return availableUserIds.map((userId) => ({
      userId,
      isGuest: groupMemberRoles.getRoleOf(userId) === 'guest',
    }));
  }
}
